import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Play, MapPin, UsersThree, User, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

// Default allowance per WHS recommendation, applied to a player's playing
// handicap before strokes-per-hole is computed.
const DEFAULT_ALLOWANCE = {
  stroke: 100, // social stroke = full handicap
  match: 100,  // individual match play
  best_ball: 85, // 4-ball best ball
  scramble: 35,  // 2-man scramble (host's submission mirrored)
};

const FORMAT_LABELS = {
  stroke: "Stroke",
  match: "Match",
  best_ball: "Best Ball",
  scramble: "Scramble",
};

export default function NewRound() {
  const { user } = useAuth();
  const myId = user?.id;
  const [courses, setCourses] = useState([]);
  const [mode, setMode] = useState("solo"); // solo | group
  const [friends, setFriends] = useState([]);
  const [groupRounds, setGroupRounds] = useState([]);
  // --- Solo flow: tee picker per-course ---
  const [soloSelected, setSoloSelected] = useState(null); // course id
  const [soloTee, setSoloTee] = useState(null);           // tee name
  // Optional score goal per the Goal Rounds feature — null = no goal, otherwise
  // a number like 100/90/80/70 or 0 for "level par".
  const [soloTarget, setSoloTarget] = useState(null);
  // --- Group flow ---
  const [groupForm, setGroupForm] = useState({
    courseId: null, teeName: "Blue", format: "stroke",
    playerIds: [], allowancePct: 100, teams: {}, // teams: {user_id: "A"|"B"}
  });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
    api.get("/friends").then((r) => setFriends(r.data)).catch(() => {});
    api.get("/group-rounds").then((r) => setGroupRounds(r.data.filter((g) => g.status === "in_progress"))).catch(() => {});
  }, []);

  const togglePlayer = (id) =>
    setGroupForm((f) => {
      const picked = f.playerIds.includes(id);
      const playerIds = picked ? f.playerIds.filter((x) => x !== id) : [...f.playerIds, id];
      const teams = { ...f.teams };
      if (picked) {
        delete teams[id];
      } else if (f.format === "best_ball" || f.format === "scramble") {
        // Auto-balance: drop newcomer onto the smaller team.
        const counts = { A: 0, B: 0 };
        Object.values(teams).forEach((t) => { counts[t] = (counts[t] || 0) + 1; });
        teams[id] = (counts.A <= counts.B) ? "A" : "B";
      }
      return { ...f, playerIds, teams };
    });

  const setPlayerTeam = (id, team) =>
    setGroupForm((f) => ({ ...f, teams: { ...f.teams, [id]: team } }));

  const pickFormat = (fmt) =>
    setGroupForm((f) => {
      const next = {
        ...f, format: fmt,
        allowancePct: DEFAULT_ALLOWANCE[fmt] ?? 100,
      };
      if (fmt === "best_ball" || fmt === "scramble") {
        // Seed host's team to A and auto-balance any already-picked players.
        const teams = { [myId]: "A" };
        let aCount = 1;
        let bCount = 0;
        f.playerIds.forEach((pid) => {
          if (aCount <= bCount) { teams[pid] = "A"; aCount += 1; }
          else { teams[pid] = "B"; bCount += 1; }
        });
        next.teams = teams;
      } else {
        next.teams = {};
      }
      return next;
    });

  const startGroup = async () => {
    const { courseId, teeName, format, playerIds, allowancePct, teams } = groupForm;
    if (!courseId) { toast.error("Pick a course"); return; }
    if (format === "match" && playerIds.length !== 1) {
      toast.error("Match play needs exactly 1 opponent"); return;
    }
    if (format === "best_ball" && playerIds.length !== 3) {
      toast.error("Best ball needs you + 3 friends (2v2)"); return;
    }
    if (format === "scramble" && playerIds.length < 1) {
      toast.error("Scramble needs at least 1 teammate or opponent"); return;
    }
    if (format === "stroke" && playerIds.length === 0) {
      toast.error("Add at least one friend"); return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/group-rounds", {
        course_id: courseId, tee_name: teeName,
        format, player_ids: playerIds,
        match_allowance_pct: Number(allowancePct),
        teams,
      });
      toast.success("Group round started");
      navigate(`/group-rounds/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    } finally { setCreating(false); }
  };

  const startSolo = (courseId, teeName) => {
    const tee = encodeURIComponent(teeName || "Blue");
    const qs = soloTarget ? `&target=${soloTarget}` : "";
    navigate(`/rounds/play/${courseId}?tee=${tee}${qs}`);
  };

  const groupCourse = courses.find((c) => c.id === groupForm.courseId);

  return (
    <div className="space-y-6">
      <div className="rise">
        <span className="label-eyebrow">New round</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Pick your card.</h1>
      </div>

      <div className="flex gap-2" data-testid="mode-toggle">
        <button onClick={() => setMode("solo")} data-testid="mode-solo"
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 ${
            mode === "solo" ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8]"
          }`}><User size={14} weight="bold" /> Solo</button>
        <button onClick={() => setMode("group")} data-testid="mode-group"
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 ${
            mode === "group" ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8]"
          }`}><UsersThree size={14} weight="bold" /> Group</button>
      </div>

      {mode === "solo" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="solo-courses-grid">
          {courses.map((c) => {
            const isSelected = soloSelected === c.id;
            const selectedTee = isSelected ? soloTee : null;
            return (
              <div
                key={c.id}
                data-testid={`select-course-${c.id}`}
                className={`card-interactive p-5 text-left transition ${isSelected ? "ring-2 ring-[#E05D3A]" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (isSelected) {
                      setSoloSelected(null);
                      setSoloTee(null);
                    } else {
                      // Default to "Blue" if available, otherwise the first tee.
                      const defaultTee = c.tees.find((t) => t.name === "Blue")?.name || c.tees[0]?.name;
                      setSoloSelected(c.id);
                      setSoloTee(defaultTee);
                    }
                  }}
                  className="w-full flex items-center gap-4 text-left"
                  data-testid={`select-course-toggle-${c.id}`}
                >
                  <img src={c.image_url} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 text-xs font-semibold text-[#0F2537]/60">
                      <MapPin size={12} weight="fill" /> {c.location}
                    </div>
                    <h3 className="font-heading font-bold truncate">{c.name}</h3>
                    <div className="text-xs text-[#0F2537]/70 mt-1">Par {c.par} · {c.tees.length} tees</div>
                  </div>
                  {isSelected
                    ? <CaretRight weight="bold" className="text-[#E05D3A] rotate-90 transition-transform" size={22} />
                    : <Play weight="fill" className="text-[#E05D3A]" size={22} />}
                </button>

                {isSelected && (
                  <div className="mt-4 pt-4 border-t border-[#E2E0D8] space-y-3" data-testid={`solo-tee-picker-${c.id}`}>
                    <div className="text-xs font-bold text-[#0F2537]/60 uppercase tracking-wider">Pick your tee</div>
                    <div className="flex flex-wrap gap-2">
                      {c.tees.map((t) => {
                        const active = selectedTee === t.name;
                        return (
                          <button
                            key={t.name}
                            type="button"
                            onClick={() => setSoloTee(t.name)}
                            data-testid={`solo-tee-${c.id}-${t.name.toLowerCase()}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition border ${
                              active
                                ? "bg-[#0F2537] text-white border-[#0F2537]"
                                : "bg-white text-[#0F2537] border-[#E2E0D8] hover:border-[#0F2537]"
                            }`}
                          >
                            <div className="leading-tight text-left">
                              <div>{t.name}</div>
                              <div className={`text-[10px] font-bold ${active ? "text-white/70" : "text-[#0F2537]/50"}`}>
                                CR {t.course_rating} · Slope {t.slope_rating}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => startSolo(c.id, selectedTee)}
                      data-testid={`solo-start-${c.id}`}
                      className="w-full py-2.5 rounded-xl bg-[#E05D3A] text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 hover:brightness-110 transition"
                    >
                      <Play weight="fill" size={14} /> Start round · {selectedTee} tees{soloTarget ? ` · goal ${soloTarget === c.par ? "level par" : soloTarget}` : ""}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mode === "solo" && soloSelected && (
        <div className="card-default p-5 space-y-3" data-testid="solo-target-picker">
          <div>
            <span className="label-eyebrow">Optional · Goal Round</span>
            <h3 className="font-heading font-bold mt-0.5">Set a target score</h3>
            <p className="text-xs text-[#0F2537]/60 mt-1">
              Declare a score to chase. We&rsquo;ll show &ldquo;on pace&rdquo; feedback during the round
              and the smart-club pick will switch to a more conservative style when
              your goal is at risk.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { v: null, label: "No goal" },
              { v: 100, label: "Break 100" },
              { v: 90,  label: "Break 90" },
              { v: 80,  label: "Break 80" },
              { v: 70,  label: "Break 70" },
            ].map((opt) => {
              const active = soloTarget === opt.v;
              return (
                <button
                  key={String(opt.v)}
                  type="button"
                  data-testid={`solo-target-${opt.v ?? "none"}`}
                  onClick={() => setSoloTarget(opt.v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition border ${
                    active
                      ? "bg-[#E05D3A] text-white border-[#E05D3A]"
                      : "bg-white text-[#0F2537] border-[#E2E0D8] hover:border-[#E05D3A]"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {mode === "group" && (
        <div className="space-y-5">
          {groupRounds.length > 0 && (
            <div className="card-default p-5">
              <span className="label-eyebrow">Continue a group round</span>
              <ul className="mt-3 divide-y divide-[#E2E0D8]">
                {groupRounds.map((g) => (
                  <li key={g.id}>
                    <Link to={`/group-rounds/${g.id}`} className="flex items-center justify-between py-3 hover:bg-black/[0.02] -mx-2 px-2 rounded-lg" data-testid={`continue-group-${g.id}`}>
                      <div>
                        <div className="font-semibold">{g.course_name}</div>
                        <div className="text-xs text-[#0F2537]/60">
                          {FORMAT_LABELS[g.format] || g.format} · {g.players.length} players · started {new Date(g.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Play size={20} weight="fill" className="text-[#E05D3A]" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="card-default p-5 md:p-6">
            <span className="label-eyebrow">Start a new group round</span>
            <h2 className="font-heading font-bold text-xl mt-1">Pick course, tee, format & players</h2>

            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Course</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {courses.map((c) => (
                  <button key={c.id} onClick={() => setGroupForm({ ...groupForm, courseId: c.id })}
                    data-testid={`group-course-${c.id}`}
                    className={`text-left p-3 rounded-xl border ${groupForm.courseId === c.id ? "border-[#0F2537] bg-[#0F2537]/5" : "border-[#E2E0D8]"}`}>
                    <div className="font-bold text-sm">{c.name}</div>
                    <div className="text-xs text-[#0F2537]/60">{c.location}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-semibold mb-2">Tee</div>
                <div className="flex flex-wrap gap-2">
                  {(groupCourse?.tees || []).map((t) => (
                    <button key={t.name} onClick={() => setGroupForm({ ...groupForm, teeName: t.name })}
                      data-testid={`group-tee-${t.name.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold ${groupForm.teeName === t.name ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8]"}`}>
                      {t.name}
                    </button>
                  ))}
                  {!groupCourse && (
                    <span className="text-xs text-[#0F2537]/50">Pick a course first to see its tees.</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Format</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(FORMAT_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => pickFormat(key)}
                      data-testid={`group-fmt-${key}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                        groupForm.format === key ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8]"
                      }`}
                    >{label}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Allowance slider — applied to every player's playing handicap. */}
            <div className="mt-5 p-4 rounded-xl bg-[#F8F7F3] border border-[#E2E0D8]" data-testid="allowance-row">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold">Handicap allowance</div>
                  <div className="text-[11px] text-[#0F2537]/60">
                    WHS suggests {DEFAULT_ALLOWANCE[groupForm.format]}% for {FORMAT_LABELS[groupForm.format]}.
                  </div>
                </div>
                <div className="inline-flex items-center gap-2">
                  <input
                    type="range" min="0" max="150" step="5"
                    value={groupForm.allowancePct}
                    onChange={(e) => setGroupForm({ ...groupForm, allowancePct: Number(e.target.value) })}
                    data-testid="allowance-slider"
                    className="w-40 accent-[#E05D3A]"
                  />
                  <span data-testid="allowance-value" className="font-heading font-black text-xl w-12 text-right">{groupForm.allowancePct}%</span>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <div className="text-sm font-semibold mb-2">
                {(() => {
                  if (groupForm.format === "match") return "Players (you + 1 opponent)";
                  if (groupForm.format === "best_ball") return "Players (you + 3 friends · 2v2)";
                  if (groupForm.format === "scramble") return "Players (you + teammates)";
                  return "Players (you + up to 3 friends)";
                })()}
              </div>
              {friends.length === 0 ? (
                <div className="text-sm text-[#0F2537]/60 p-4 bg-[#F8F7F3] rounded-xl">
                  No friends yet. <Link to="/friends" className="text-[#E05D3A] font-bold">Add some →</Link>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {friends.map((f) => {
                    const picked = groupForm.playerIds.includes(f.id);
                    const team = groupForm.teams[f.id];
                    const showTeams = picked && (groupForm.format === "best_ball" || groupForm.format === "scramble");
                    return (
                      <div
                        key={f.id}
                        className={`p-3 rounded-xl border ${picked ? "border-[#E05D3A] bg-[#E05D3A]/5" : "border-[#E2E0D8] bg-white"}`}
                        data-testid={`group-player-row-${f.id}`}
                      >
                        <button onClick={() => togglePlayer(f.id)} data-testid={`group-player-${f.id}`}
                          type="button"
                          className="w-full text-left flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#0F2537] text-white flex items-center justify-center font-bold text-sm">
                            {f.name?.slice(0, 1).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm truncate">{f.name}</div>
                            <div className="text-xs text-[#0F2537]/60">Hcp {f.handicap_index ?? "—"}</div>
                          </div>
                          {picked && <div className="text-[#E05D3A] font-bold">✓</div>}
                        </button>
                        {showTeams && (
                          <div className="mt-2 flex items-center gap-1.5 pl-12" data-testid={`team-toggle-${f.id}`}>
                            {["A", "B"].map((t) => (
                              <button key={t} type="button"
                                onClick={() => setPlayerTeam(f.id, t)}
                                data-testid={`team-${t.toLowerCase()}-${f.id}`}
                                className={`flex-1 py-1 rounded-lg text-[11px] font-bold transition ${
                                  team === t
                                    ? (t === "A" ? "bg-[#E05D3A] text-white" : "bg-[#0F2537] text-white")
                                    : "bg-white border border-[#E2E0D8] text-[#0F2537]/70"
                                }`}
                              >Team {t}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Host's team toggle for best_ball / scramble */}
              {(groupForm.format === "best_ball" || groupForm.format === "scramble") && (
                <div className="mt-3 p-3 rounded-xl bg-[#F8F7F3] flex items-center gap-3" data-testid="host-team-row">
                  <span className="text-xs font-bold text-[#0F2537]/70 flex-1">Your team</span>
                  {["A", "B"].map((t) => {
                    const active = (groupForm.teams[myId] || "A") === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        data-testid={`host-team-${t.toLowerCase()}`}
                        onClick={() => setPlayerTeam(myId, t)}
                        className={`px-3 py-1 rounded-lg text-[11px] font-bold transition ${
                          active
                            ? (t === "A" ? "bg-[#E05D3A] text-white" : "bg-[#0F2537] text-white")
                            : "bg-white border border-[#E2E0D8] text-[#0F2537]/70"
                        }`}
                      >Team {t}</button>
                    );
                  })}
                </div>
              )}
            </div>

            {groupForm.format === "scramble" && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-[#0F2537]/5 text-[11px] text-[#0F2537]/70 leading-relaxed" data-testid="scramble-hint">
                <strong>Scramble:</strong> any teammate's strokes entered on a hole are mirrored to the rest of the team. Pick one of you to "captain" the card and enter each hole's team score.
              </div>
            )}

            <button onClick={startGroup} disabled={creating} data-testid="start-group-round-btn"
              className="mt-6 w-full py-3 rounded-xl bg-[#E05D3A] text-white font-bold disabled:opacity-60">
              {creating ? "Starting…" : "Start group round"}
            </button>
          </div>
        </div>
      )}

      <p className="text-sm text-[#0F2537]/60"><Link to="/dashboard" className="hover:text-[#E05D3A]">← Back to home</Link></p>
    </div>
  );
}
